/**
 * FeedbackWidget
 *
 * A floating feedback button that opens a compact modal for submitting
 * bug reports, feature requests, and general feedback. Designed to be
 * nondescript and unobtrusive, using the existing design system tokens.
 *
 * Place this component once in the root layout so it appears on every page.
 */

import { Component, createSignal, Show, onCleanup } from "solid-js";
import { api } from "../utils/apiClient";
import { trace } from "../lib/trace";

type FeedbackCategory = "bug" | "feature" | "general";

interface FeedbackPayload {
  category: FeedbackCategory;
  message: string;
  page: string;
  timestamp: string;
}

const CATEGORIES: { value: FeedbackCategory; label: string }[] = [
  { value: "bug", label: "Bug Report" },
  { value: "feature", label: "Feature Request" },
  { value: "general", label: "General Feedback" },
];

export const FeedbackWidget: Component = () => {
  const [isOpen, setIsOpen] = createSignal(false);
  const [category, setCategory] = createSignal<FeedbackCategory>("general");
  const [message, setMessage] = createSignal("");
  const [isSubmitting, setIsSubmitting] = createSignal(false);
  const [showSuccess, setShowSuccess] = createSignal(false);
  const [errorText, setErrorText] = createSignal<string | null>(null);

  let successTimeout: ReturnType<typeof setTimeout> | undefined;
  let dialogRef: HTMLDivElement | undefined;
  let previousFocusRef: HTMLElement | undefined;

  onCleanup(() => {
    if (successTimeout) clearTimeout(successTimeout);
  });

  const resetForm = () => {
    setCategory("general");
    setMessage("");
    setErrorText(null);
  };

  const handleOpen = () => {
    previousFocusRef = document.activeElement as HTMLElement | undefined;
    setIsOpen(true);
    // Auto-focus the category select after render
    requestAnimationFrame(() => {
      const el = dialogRef?.querySelector<HTMLElement>("select, input, textarea, button");
      el?.focus();
    });
  };

  const handleClose = () => {
    setIsOpen(false);
    resetForm();
    // Restore focus to the trigger element
    previousFocusRef?.focus();
  };

  const handleSubmit = async () => {
    const trimmed = message().trim();
    if (!trimmed) {
      setErrorText("Please enter a message.");
      return;
    }

    setIsSubmitting(true);
    setErrorText(null);

    const payload: FeedbackPayload = {
      category: category(),
      message: trimmed,
      page: window.location.href,
      timestamp: new Date().toISOString(),
    };

    try {
      trace.api("POST /api/feedback", { category: payload.category });
      await api.post("/api/feedback", payload);
      trace.api("Feedback submitted successfully", { category: payload.category });
      setIsSubmitting(false);
      setIsOpen(false);
      resetForm();
      setShowSuccess(true);
      successTimeout = setTimeout(() => setShowSuccess(false), 3000);
    } catch {
      trace.api("Feedback submission failed", { category: payload.category }, "error");
      setIsSubmitting(false);
      setErrorText("Failed to send feedback. Please try again.");
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && isOpen()) {
      handleClose();
      return;
    }
    // Focus trap: cycle Tab/Shift+Tab within the dialog
    if (e.key === "Tab" && isOpen() && dialogRef) {
      const focusable = dialogRef.querySelectorAll<HTMLElement>(
        'button:not([disabled]), select, textarea, input, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  };

  /** Prevent clicks inside the modal from closing it */
  const handleBackdropClick = (e: MouseEvent) => {
    if ((e.target as HTMLElement).dataset.backdrop === "true") {
      handleClose();
    }
  };

  return (
    <>
      {/* --- Trigger Button --- */}
      <Show when={!isOpen()}>
        <button
          type="button"
          onClick={handleOpen}
          aria-label="Send feedback"
          title="Send feedback"
          class="fbw-trigger"
        >
          {/* Chat bubble icon */}
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      </Show>

      {/* --- Modal Backdrop + Panel --- */}
      <Show when={isOpen()}>
        <div
          ref={dialogRef}
          data-backdrop="true"
          onClick={handleBackdropClick}
          onKeyDown={handleKeyDown}
          role="dialog"
          aria-modal="true"
          aria-label="Send feedback"
          class="fbw-backdrop"
        >
          <div class="fbw-panel">
            {/* Header */}
            <div class="fbw-header">
              <span class="fbw-header__title">
                Send Feedback
              </span>
              <button
                type="button"
                onClick={handleClose}
                aria-label="Close feedback form"
                class="fbw-header__close"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div class="fbw-body">
              {/* Category */}
              <div class="fbw-field">
                <label
                  for="feedback-category"
                  class="fbw-field__label"
                >
                  Category
                </label>
                <select
                  id="feedback-category"
                  value={category()}
                  onChange={(e) => setCategory(e.currentTarget.value as FeedbackCategory)}
                  class="fbw-select"
                >
                  {CATEGORIES.map((cat) => (
                    <option value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div class="fbw-field">
                <label
                  for="feedback-message"
                  class="fbw-field__label"
                >
                  Message
                </label>
                <textarea
                  id="feedback-message"
                  placeholder="Tell us what you think..."
                  value={message()}
                  aria-required="true"
                  aria-describedby={errorText() ? "feedback-error" : undefined}
                  onInput={(e) => {
                    setMessage(e.currentTarget.value);
                    if (errorText()) setErrorText(null);
                  }}
                  rows={4}
                  class="fbw-textarea"
                />
              </div>

              {/* Error */}
              <Show when={errorText()}>
                <p
                  id="feedback-error"
                  role="alert"
                  class="fbw-error"
                >
                  {errorText()}
                </p>
              </Show>
            </div>

            {/* Footer */}
            <div class="fbw-footer">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting()}
                class="fbw-btn-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting()}
                class="fbw-btn-submit"
                style={{
                  background: isSubmitting() ? "var(--color-gray-400, #9ca3af)" : undefined,
                  cursor: isSubmitting() ? "not-allowed" : undefined,
                  opacity: isSubmitting() ? "0.7" : undefined,
                }}
              >
                {isSubmitting() ? "Sending..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      </Show>

      {/* --- Success Toast --- */}
      <Show when={showSuccess()}>
        <div
          role="status"
          aria-live="polite"
          class="fbw-toast"
        >
          Feedback sent — thank you!
        </div>
      </Show>
    </>
  );
};
